import { Icon } from '@starryui10/images/Icon';
import React, { useEffect, useState } from 'react';
import { Text } from '@starryui10/typography/Text';
import { Spinner } from '@starryui10/Loading/Spinner';
import styled from 'styled-components';
import { styles } from '@starryui10/theme/styles';
import { color } from '@starryui10/theme/color';
import { ButtonHTMLAttributes } from 'react';
import { Tick } from '@starryui10/images/icons/Tick';
import { createRef } from 'react';

export interface IButtonProps extends global.HashableComponent, ButtonHTMLAttributes<HTMLButtonElement> {
    compact?: boolean;
    enableCompletedState?: boolean;
    enableLoadingState?: boolean;
    fullWidth?: boolean;
    icon?: image.IconVariants;
    iconPosition?: buttons.IconPosition;
    loadingLabel?: string;
    type?: 'button' | 'reset' | 'submit';
    variant?: buttons.ButtonVariant;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => Promise<any> | void;
}

const iconSize = '22px';

type LoadingStates = 'Normal' | 'Loading' | 'Completed';

export const Button = React.forwardRef<HTMLButtonElement, IButtonProps>((props, ref) => {
    const {
        addHash,
        children,
        enableLoadingState,
        enableCompletedState,
        icon,
        iconPosition,
        loadingLabel = 'Loading',
        type = 'button',
        variant = 'Primary',
        onClick,
        ...buttonProps
    } = props;

    const [loadingState, setLoadingState] = useState<LoadingStates>('Normal');
    const buttonRef = createRef<HTMLDivElement>();

    useEffect(() => {
        if (enableLoadingState && buttonRef && typeof buttonRef === 'object' && buttonRef.current) {
            const size = buttonRef.current.getBoundingClientRect();
            buttonRef.current.style.width = `${size.width}px`;
            buttonRef.current.style.maxWidth = `${size.width}px`;
        }
    }, []);

    useEffect(() => {
        if (enableLoadingState && buttonRef && typeof buttonRef === 'object' && buttonRef.current && loadingState === 'Normal') {
            buttonRef.current.style.width = ``;
            buttonRef.current.style.maxWidth = ``;

            const size = buttonRef.current.getBoundingClientRect();
            buttonRef.current.style.width = `${size.width}px`;
            buttonRef.current.style.maxWidth = `${size.width}px`;
        }
    }, [loadingState, children]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick && !buttonProps.disabled && loadingState === 'Normal') {
            // If this returns something then it is a promise
            const clickResponse = onClick(e);

            if (clickResponse && enableLoadingState) {
                setLoadingState('Loading');

                clickResponse
                    .then(() => {
                        if (enableCompletedState) {
                            setLoadingState('Completed');
                        }
                    })
                    .catch(() => {
                        setLoadingState('Normal');
                    })
                    .finally(() => {
                        window.setTimeout(() => {
                            setLoadingState('Normal');
                        }, 2000);
                    });
            }
        }
    };

    return (
        <div ref={buttonRef}>
            <StyledButton {...buttonProps} state={loadingState} ref={ref} type={type} variant={variant} onClick={handleClick}>
                {loadingState === 'Normal' && (
                    <>
                        {icon && (
                            <IconWrapper iconPosition={iconPosition}>
                                <Icon variant={icon} height={iconSize} width={iconSize} />
                            </IconWrapper>
                        )}
                        {children && (
                            <Text addHash={addHash} variant="normal.bold" color="inherit">
                                {children}
                            </Text>
                        )}
                    </>
                )}

                {loadingState === 'Completed' && <Tick height={iconSize} width={iconSize} />}

                {loadingState === 'Loading' && <Spinner size={'18px'} color="white" />}
            </StyledButton>
        </div>
    );
});

interface IIconWrapperProps {
    iconPosition: buttons.IconPosition | undefined;
}

const iconSpacing = '7px';

const IconWrapper = styled.span<IIconWrapperProps>`
    position: relative;
    display: flex;
    align-items: center;
    height: 18px;
    width: 18px;
    ${({ iconPosition }) => (iconPosition === 'Right' ? 'order: 1;' : '')}

    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

interface IStylingProps {
    variant?: buttons.ButtonVariant;
    compact?: boolean;
    state: LoadingStates;
    fullWidth?: boolean;
}

const StyledButton = styled.button<IStylingProps>`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: min-content;
    align-items: center;
    justify-content: center;
    gap: ${iconSpacing};
    white-space: nowrap;
    height: ${({ compact }) => (compact ? '32px' : '40px')};
    padding: ${({ compact }) => (compact ? '0 12px' : '0 20px')};
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    border-radius: ${() => styles.borderRadius};
    cursor: pointer;
    border: 0;
    outline: 0;
    transition: background ease-in-out 0.2s;

    svg,
    span {
        cursor: pointer;
    }

    &:disabled {
        cursor: not-allowed;
    }

    &:focus {
        outline: 2px solid ${color.StarRezBrandColors.WhiteSR};
        outline-offset: -4px;
    }

    &:not(:disabled):hover {
        outline: 0;
    }

    ${(props) => buttonStateStyles(props)}
`;

const buttonStateStyles = ({ variant, compact, state }: IStylingProps) => {
    if (state === 'Completed') {
        return `
            color: ${color.StarRezBrandColors.WhiteSR};
            background-color: ${color.Green.G60};
        
            &:disabled {
                background-color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                background-color: ${color.Green.G80};
            }
        
            &:focus {
                background-color: ${color.Green.G80};
            }
        
            &:not(:disabled):active {
                background-color: ${color.Green.G100};
            }
        `;
    }

    if (variant === 'Primary') {
        return `
            color: ${color.StarRezBrandColors.WhiteSR};
            background-color: ${color.Blue.B60};
        
            &:disabled {
                background-color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                background-color: ${color.Blue.B80};
            }
        
            &:focus {
                background-color: ${color.Blue.B80};
            }
        
            &:not(:disabled):active {
                background-color: ${color.Blue.B100};
            }
        `;
    }

    if (variant === 'Secondary') {
        return `
            color: ${color.Blue.B60};
            background-color: ${color.Grey.G20};
        
            &:disabled {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
            }
        
            &:focus {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
            }
        
            &:not(:disabled):active {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B100};
            }
        `;
    }

    if (variant === 'Tertiary') {
        return `
            padding: ${compact ? '6px 12px' : '10px 20px'};
            color: ${color.Blue.B60};
            background-color: ${color.StarRezBrandColors.WhiteSR};
            border: 1px solid ${color.Grey.G40};
        
            &:disabled {
                color: ${color.Grey.G50};
                background-color: ${color.StarRezBrandColors.WhiteSR};
            }
        
            &:not(:disabled):hover {
                color: ${color.Blue.B80};
                background-color: ${color.Grey.G10};
            }
        
            &:focus {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
                border: 1px solid ${color.Blue.B80};
            }
        
            &:not(:disabled):active {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B100};
                border: 1px solid ${color.Blue.B100};
            }
        `;
    }

    if (variant === 'Danger') {
        return `
            color: ${color.StarRezBrandColors.WhiteSR};
            background-color: ${color.Red.R60};
        
            &:disabled {
                background-color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                background-color: ${color.Red.R80};
            }
        
            &:focus {
                background-color: ${color.Red.R80};
            }
        
            &:not(:disabled):active {
                background-color: ${color.Red.R100};
            }
        `;
    }

    if (variant === 'Ghost') {
        return `
            color: ${color.Blue.B60};
            background-color: transparent;
        
            &:disabled {
                color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                color: ${color.Blue.B80};
                background-color: ${color.Grey.G20};
            }
        
            &:focus {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
            }
        
            &:not(:disabled):active {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B100};
            }
        `;
    }

    if (variant === 'SecondaryReversed') {
        return `
            color: ${color.Blue.B60};
            background-color: ${color.StarRezBrandColors.WhiteSR};
        
            &:disabled {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Grey.G50};
            }
        
            &:not(:disabled):hover {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
            }
        
            &:focus {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B80};
            }
        
            &:not(:disabled):active {
                color: ${color.StarRezBrandColors.WhiteSR};
                background-color: ${color.Blue.B100};
            }
        `;
    }

    return '';
};
